import config from '@/config';
import RatingsList from '@/src/components/CustomerRatings/RatingsList';
import { DataProvider } from '@/src/components/ProductContext';
import ComponentProvider from '@empiriecom/module-components/ComponentProvider';
import ConfigProvider from '@empiriecom/module-components/ConfigProvider';
import { Locale } from '@empiriecom/module-components/IntlProvider';
import LocaleSwitcher from '@empiriecom/module-components/LocaleSwitcher';
import '@formatjs/intl-pluralrules/polyfill-locales';
import React, { FC } from 'react';
import { hot } from 'react-hot-loader';
import { Route, Switch } from 'react-router-dom';
import messages from '../../../locales';
import PageContainer from '../PageContainer';
import ReloadLink from '../ReloadLink';
import ErrorRedirect from './ErrorRedirect';
import CompactDetailView from '../CompactDetailView';

const locales = Object.keys(messages) as Locale[];

/**
 * Route component that utilizes render prop to keep rendered component from remounting on every render.
 * This component wraps the rendered Component with a DataProvider.
 */
const DataRoute = ({ component: Component, productId, isBot, ...rest }) => (
  <Route
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...rest}
    render={() => (
      <DataProvider productId={productId} isBot={isBot}>
        <Component />
      </DataProvider>
    )}
  />
);

type AppProps = {
  device?: 'mobile' | 'desktop';
  locale?: Locale;
  productId?: string;
  isBot?: boolean;
};

const App: FC<AppProps> = ({ device, locale, productId, isBot = false }): JSX.Element => {
  try {
    return (
      <ConfigProvider config={config}>
        <ComponentProvider
          data-testid="app-component-provider"
          messages={messages}
          fallbackLocale={locale || config.page.values.defaultLocale}
          mediaOverwrite={device}
        >
          {/* We need Fragment (<>...</>) here for Styled Components rendering */}
          <>
            <Switch>
              {config.customerRatings.enabled && (
                <DataRoute
                  isBot={isBot}
                  path={['(/fr/p|/p)/:seoId/:productId/rating*', '(/fr/p|/p)/:productId/rating*']}
                  productId={productId}
                  component={RatingsList}
                />
              )}
              <DataRoute
                isBot={isBot}
                path={['/merkzettel/']}
                productId={productId}
                component={CompactDetailView}
              />
              <DataRoute
                isBot={isBot}
                path={['/order/']}
                productId={productId}
                component={CompactDetailView}
              />
              <DataRoute
                isBot={isBot}
                path={['(/fr/p|/p)/compact/']}
                productId={productId}
                component={CompactDetailView}
              />
              <DataRoute
                isBot={isBot}
                path={['(/fr/p|/p)/:seoId/:productId', '(/fr/p|/p)/:productId']}
                productId={productId}
                component={PageContainer}
              />
              {/* Route for potential links rendered with router Link component the should be redirected */}
              <Route path="*" component={ReloadLink} />
            </Switch>
            <LocaleSwitcher
              localesToSwitch={locales}
              fallbackLocale={config.page.values.defaultLocale}
            />
          </>
        </ComponentProvider>
      </ConfigProvider>
    );
  } catch (e) {
    return <ErrorRedirect />;
  }
};

export default hot(module)(App);
